import { HttpClient, HttpHeaders } from '@angular/common/http';
import { ApplicationRef, inject, Injectable, signal } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, exhaustMap, filter, map, take, throttleTime } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class VersionService {
  private readonly http = inject(HttpClient);
  private readonly appRef = inject(ApplicationRef);
  private readonly router = inject(Router);

  readonly currentVersion = signal('');

  private readonly versionUrl = 'version.txt';

  constructor() {
    this.checkForUpdate().subscribe();
  }

  listenForUpdates(): void {
    // Poll for updates when the app becomes idle but no faster than once per 5 min
    this.appRef.isStable
      .pipe(
        filter((isStable) => isStable),
        throttleTime(300000),
        exhaustMap(() => this.checkForUpdate()),
        filter((hasUpdate) => hasUpdate),
        take(1),
      )
      .subscribe(() => this.activateNewVersion());
  }

  private checkForUpdate(): Observable<boolean> {
    const headers = new HttpHeaders({
      'Cache-Control': 'no-cache',
      'Pragma': 'no-cache',
      'X-Hide-Loader': '1',
    });
    const url = `${this.versionUrl}?t=${Date.now()}`; // cache-buster
    return this.http.get(url, { headers, responseType: 'text' }).pipe(
      map((verRaw) => (verRaw ?? '').trim()),
      map((newVersion) => {
        if (!newVersion) {
          return false;
        }

        if (!this.currentVersion()) {
          this.currentVersion.set(newVersion);
          return false;
        }

        return newVersion !== this.currentVersion();
      }),
      catchError(() => of(false)),
    );
  }

  private activateNewVersion(): void {
    const path = this.router.url.split('?').shift() ?? '';
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe((event) => {
        const newPath = event.url.split('?').shift() ?? '';
        if (path !== newPath) {
          document.location.reload();
        }
      });
  }
}
